import React from 'react';
import { Zap, BadgeCheck, Loader2 } from 'lucide-react';
import { ScoreRing } from './ScoreRing';

interface VerdictBlockProps {
  score: number;
  /** One-line judgment, e.g. "Moderate fit — review required". */
  judgment: string;
  /** Biggest positive driver (↑). */
  driver?: string;
  /** Biggest gap / negative driver (↓). */
  gap?: string;
  /** Honesty line — names what the score is currently built on. */
  honestyLine: string;
  /** True once a cached AI deep-score exists. */
  aiVerified?: boolean;
  /** Run the costlier capabilities pass on demand. */
  onDeepScore?: () => void;
  deepScoreLoading?: boolean;
}

/**
 * Verdict Block — the number, a one-line judgment, the single biggest positive
 * and negative driver, and a short honesty line, with the optional AI deep-score
 * CTA. Engine-agnostic: it renders verdict + drivers + honesty regardless of
 * whether a cheap rule-based pass or an expensive AI pass produced them.
 */
export function VerdictBlock({
  score,
  judgment,
  driver,
  gap,
  honestyLine,
  aiVerified,
  onDeepScore,
  deepScoreLoading,
}: VerdictBlockProps) {
  return (
    <div className="bg-bw-surface-subtle border border-bw-border rounded-xl p-3.5 mb-2.5">
      <div className="flex items-start gap-3.5 mb-2.5">
        <ScoreRing score={score} size={66} showScale />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <div className="text-[13.5px] font-bold text-bw-ink leading-tight">{judgment}</div>
            {aiVerified && (
              <span className="px-2 py-0.5 bg-evidence-grounded-bg text-evidence-grounded border border-evidence-grounded-border text-[10px] font-bold rounded-full inline-flex items-center gap-1">
                <BadgeCheck className="h-3 w-3" /> AI-verified
              </span>
            )}
          </div>
          <div className="text-[11.5px] text-bw-muted leading-relaxed">
            {driver && (
              <div className="mb-0.5">
                <span className="text-fit-strong font-bold">↑ </span>
                {driver}
              </div>
            )}
            {gap && (
              <div>
                <span className="text-bw-accent font-bold">↓ </span>
                {gap}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="text-[11px] text-bw-muted px-2.5 py-2 bg-bw-surface rounded-md border border-dashed border-bw-border leading-snug mb-2.5">
        {honestyLine}
      </div>

      {!aiVerified && onDeepScore && (
        <button
          onClick={onDeepScore}
          disabled={deepScoreLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-transparent border-[1.5px] border-bw-primary rounded-md text-bw-primary text-[11.5px] font-semibold hover:bg-bw-primary-tint transition-colors disabled:opacity-60"
        >
          {deepScoreLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
          {deepScoreLoading ? 'Running AI deep-score…' : 'Run AI deep-score for capabilities fit'}
        </button>
      )}
    </div>
  );
}

export default VerdictBlock;
